import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import fetchData from '../Utilis/request';
import { ArticleShare, Loader, RelatedArticles } from '../components/Index';
import { useAppSelector } from '../store/hooks';
import { ArticleDetailsProps, initailArticleState } from '../types/typedeclaration'; // Assuming these types are correct
import { useSwipe } from '../store/useSwipe'; // Assuming this hook exists
import { useReadArticles } from '../hooks/useReadArticles';
import { HEADING_STYLE_DARK_MODE, HEADING_STYLE_LIGHT_MODE } from '../Config/config';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// --- IMPORTANT: Extend the Window interface for TypeScript ---
// Add this declaration at the end of this file, or in a global .d.ts file (e.g., src/types/global.d.ts)
declare global {
  interface Window {
    twttr?: {
      widgets: {
        load: (element: HTMLElement | null) => void;
        createTweet: (tweetId: string, element: HTMLElement, options?: object) => Promise<any>;
        // Add other methods if you interact with them, e.g., twttr.ready, twttr.events
      };
      // Other top-level properties of the twttr object if you use them
      // ready?: (callback: () => void) => void;
    };
    // NEW: For Instagram
    instgrm?: {
      Embeds: {
        process: () => void;
      };
    };
  }
}

const ArticleDetailPage = () => {
  const { onTouchStart, onTouchMove, onTouchEnd } = useSwipe();
  const { slug } = useParams<{ slug: string }>();
  const containerRef = useRef<HTMLDivElement>(null); // Ref for the scrollable container
  const articleContentRef = useRef<HTMLDivElement>(null); // Ref for the div holding `detailtext`
  const [article, setArticle] = useState<ArticleDetailsProps>(initailArticleState);
  const [loading, setLoading] = useState<boolean>(true);  
  const [error, setError] = useState<string | null>(null);
  const darkMode = useAppSelector((state: any) => state.categoryNavigation.darkmode);
  const headerHeight = useAppSelector((state: any) => state.categoryNavigation.headerHeight || 0);
  const fooNavHeight = useAppSelector((state: any) => state.categoryNavigation.fooNavHeight || 0);
  const { markArticleAsRead } = useReadArticles();
  // --- Helper to ensure X (Twitter) widgets.js is loaded globally ---
  const loadXWidgetsScript = useCallback(() => {
    if (!window.twttr) {
      const script = document.createElement('script');
      script.src = 'https://platform.twitter.com/widgets.js';
      script.async = true;
      script.charset = 'utf-8';
      document.body.appendChild(script);
    }
  }, []); // Memoize the function, runs only once

  // --- NEW: Helper to ensure Instagram embed.js is loaded globally ---
  const loadInstagramEmbedScript = useCallback(() => {
    if (!window.instgrm) {
      const script = document.createElement('script');
      script.src = '//www.instagram.com/embed.js'; // Use protocol-relative URL
      script.async = true;
      script.charset = 'utf-8';
      document.body.appendChild(script);
    }
  }, []); // Memoize the function, runs only once

  // --- Data Fetching Effect ---
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchData.getResult(`${API_BASE_URL}getArticleDetail.php?slug=${slug}`);       
        if (response?.articles && Object.keys(response.articles).length > 0) {
          setArticle(response);
                // --- Mark article as read after successful fetch ---                     
            if (response?.articles?.id && response?.articles?.type) {
        
        
                const articleIdToStore = response?.articles?.id;
                const articleTypeToStore = response?.articles?.type;               
                markArticleAsRead({
                    id: articleIdToStore,
                    type: articleTypeToStore
                });
            }
       
    
        } else {
          throw new Error('Article not found');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
    fetchArticle();
  }, [slug]);

  // --- Effect to load X and Instagram embeds after HTML is rendered ---
  useLayoutEffect(() => {
    if (article?.articles?.detailtext && articleContentRef.current) {
      // 1. Ensure embed scripts are loaded (if not already)
      loadXWidgetsScript();
      loadInstagramEmbedScript(); // NEW: Load Instagram script

      // 2. Clean up any existing embed scripts that might have been added by
      //    previous `dangerouslySetInnerHTML` content, to prevent duplicates.
      const twitterScripts = articleContentRef.current.querySelectorAll('script[src*="platform.twitter.com/widgets.js"]');
      twitterScripts.forEach(script => script.remove());
      const instagramScripts = articleContentRef.current.querySelectorAll('script[src*="//www.instagram.com/embed.js"]'); // NEW: Cleanup Instagram
      instagramScripts.forEach(script => script.remove());

      // 3. Trigger X's widgets.load() to process the new content
      const processXEmbeds = () => {
        if (window.twttr && window.twttr.widgets) {
          window.twttr.widgets.load(articleContentRef.current);         
        } else {        
          setTimeout(processXEmbeds, 200);
        }
      };

      // NEW: Trigger Instagram's Embeds.process()
      const processInstagramEmbeds = () => {
        if (window.instgrm && window.instgrm.Embeds) {
          // Instagram's process() typically scans the entire document,
          // but calling it after our content is in place is key.
          window.instgrm.Embeds.process();          
        } else {          
          setTimeout(processInstagramEmbeds, 200);
        }
      };

      // Start the processing for both
      processXEmbeds();
      processInstagramEmbeds(); // Call the new Instagram processor
    }
  }, [article, loadXWidgetsScript, loadInstagramEmbedScript]); // Add loadInstagramEmbedScript to dependencies

  if (loading) {
    return (<div className='relative py-32 inset-0 z-10'><Loader darkMode={darkMode} /></div>);
  }

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center min-h-screen p-4 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'}`}>
        <p className="text-xl font-semibold mb-4 text-red-500">{error}</p>
        <button
          onClick={() => window.location.reload()} // Simple reload to retry
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!article?.articles) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'}`}>
        No article data found.
      </div>
    );
  }
// const targetLocation = article?.meta_url || window.location.href;
const targetLocation = window.location.href || "";
//  const shortDetails = article?.meta_description || article?.articles?.post_short_content || '';
  return (
    <div className={`font-sans min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'}`}>

      {loading && <Loader darkMode={darkMode} />}
      {/* Main Content */}
      <main
        className="px-3 details-container"
        ref={containerRef} // This ref is for scrolling, not content manipulation
        style={{ height: `calc(100vh - ${fooNavHeight + 10}px)`, overflowY: 'auto', paddingTop: headerHeight + 10 }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}>

        {/* Article Header */}
        <article className={`rounded-lg shadow-md overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="relative">
            {article?.articles?.post_video ? (
              <div className="w-full aspect-video">
                <iframe
                  className="w-full h-full"
                  src={article?.articles?.post_video}
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            ) : (
              <img
                src={article?.articles?.image}
                alt={article?.articles?.image_alt}
                className="w-full h-auto max-h-96 object-cover"
              />
            )}
            {!article?.articles?.post_video && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                <p className="text-white text-sm">{article?.articles?.image_caption}</p>
              </div>
            )}
          </div>

          {/* Article Content */}
          <div className="px-2 py-4"> {/* Added some padding here */}
            {/* Tags */}
            {article?.articles?.post_tags && article?.articles?.post_tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {article?.articles?.post_tags?.map((tag: any, index: any) => (
                  <span key={index} className={`text-xs px-3 py-1 rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'}`}>
                    <Link to={`/search?tag=${tag}`}>{tag}</Link>
                  </span>
                ))}
              </div>
            )}

            {/* Title */}
            
            <h1 className={`text-xl md:text-xl font-bold mb-4 leading-tight ${darkMode ? HEADING_STYLE_DARK_MODE : HEADING_STYLE_LIGHT_MODE}`}>
              {article?.articles?.title}
            </h1>
            {/* Short Content */}
            <p className="text-lg mb-6 font-medium">
              {article?.articles?.post_short_content}
            </p>

            {/* Article Body (where detailtext is rendered) */}
            <div
              ref={articleContentRef} // Attach the new ref here
              className={`prose ${darkMode ? 'prose-invert' : ''} max-w-none mb-8 myPostDetails`}
              dangerouslySetInnerHTML={{ __html: article?.articles?.detailtext || '' }} // Added empty string fallback
            />

            {/* Social Share */}
           
           <ArticleShare darkMode={darkMode} title={article?.articles?.title || ''} location={targetLocation}/>


            {/* Reporter Info */}
            {article?.articles?.reporter_data && article?.articles?.reporter_data.length > 0 && (
              <div className="flex items-center mb-8">
                <div className="w-12 h-12 rounded-full mr-4 overflow-hidden flex-shrink-0">
                  {article.articles.reporter_data[0].image ? (
                    <img src={article.articles.reporter_data[0].image} alt={article.articles.reporter_data[0].reporter_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 text-lg font-semibold">
                      {article.articles.reporter_data[0].reporter_name.charAt(0)}
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="font-bold">{article.articles.reporter_data[0].reporter_name}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Reporter at Bharat24</p>
                </div>
              </div>
            )}
          </div>
        </article>

        {/* You May Also Like */}
        {Array.isArray(article?.storiesyoumayinterested) && article?.storiesyoumayinterested?.length > 0 && (<RelatedArticles article={article} />)}
      </main>

    </div>
  );
};

export default ArticleDetailPage;